import React from "react";
import { Form, Row, Col } from "react-bootstrap";
import { Autocomplete, Box } from "@mui/material";
import { ReactCountryFlag } from "react-country-flag";
import BootstrapTextField from "Common/BootstrapTextField";
import { autocompleteDropdownStyles } from "Constants/Style";

// Updated interface to make country fields optional
interface PhoneInputProps {
  countryValue?: string;
  phoneValue: string;
  onCountryChange?: (value: string) => void;
  onPhoneChange: (value: string) => void;
  onCountryBlur?: () => void;
  onPhoneBlur?: () => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  countryInvalid?: boolean;
  phoneInvalid?: boolean;
  countryErrorMessage?: string;
  phoneErrorMessage?: string;
  style?: React.CSSProperties;
  disabled?: boolean;
  name?: string;
}

const PhoneInputComponent: React.FC<PhoneInputProps> = ({
  countryValue = "+91",
  phoneValue,
  onCountryChange,
  onPhoneChange,
  onCountryBlur,
  onPhoneBlur,
  label = "Phone Number",
  placeholder = "Enter phone number",
  required = false,
  countryInvalid = false,
  phoneInvalid = false,
  countryErrorMessage,
  phoneErrorMessage,
  style,
  disabled = false,
  name,
}) => {
  const showCountrySelector =
    countryValue !== undefined && onCountryChange !== undefined;
  const labelStyle = {
    fontSize: "0.8rem",
    marginBottom: 6,
    marginTop: 12,
  };

  const inputStyle = {
    fontSize: "0.8rem",
    minHeight: 38,
    padding: "0.375rem 0.75rem",
    ...style,
  };

  const countries = [
    {
      code: "+1",
      name: "United States",
      flag: "US",
      displayName: "United States (+1)",
    },
    { code: "+91", name: "India", flag: "IN", displayName: "India (+91)" },
    {
      code: "+44",
      name: "United Kingdom",
      flag: "GB",
      displayName: "United Kingdom (+44)",
    },
    {
      code: "+61",
      name: "Australia",
      flag: "AU",
      displayName: "Australia (+61)",
    },
    { code: "+81", name: "Japan", flag: "JP", displayName: "Japan (+81)" },
    { code: "+86", name: "China", flag: "CN", displayName: "China (+86)" },
    { code: "+49", name: "Germany", flag: "DE", displayName: "Germany (+49)" },
    { code: "+33", name: "France", flag: "FR", displayName: "France (+33)" },
    { code: "+39", name: "Italy", flag: "IT", displayName: "Italy (+39)" },
    { code: "+7", name: "Russia", flag: "RU", displayName: "Russia (+7)" },
    { code: "+55", name: "Brazil", flag: "BR", displayName: "Brazil (+55)" },
    {
      code: "+27",
      name: "South Africa",
      flag: "ZA",
      displayName: "South Africa (+27)",
    },
    {
      code: "+82",
      name: "South Korea",
      flag: "KR",
      displayName: "South Korea (+82)",
    },
    {
      code: "+65",
      name: "Singapore",
      flag: "SG",
      displayName: "Singapore (+65)",
    },
    { code: "+971", name: "UAE", flag: "AE", displayName: "UAE (+971)" },
    {
      code: "+966",
      name: "Saudi Arabia",
      flag: "SA",
      displayName: "Saudi Arabia (+966)",
    },
    {
      code: "+60",
      name: "Malaysia",
      flag: "MY",
      displayName: "Malaysia (+60)",
    },
    {
      code: "+63",
      name: "Philippines",
      flag: "PH",
      displayName: "Philippines (+63)",
    },
    {
      code: "+66",
      name: "Thailand",
      flag: "TH",
      displayName: "Thailand (+66)",
    },
    { code: "+84", name: "Vietnam", flag: "VN", displayName: "Vietnam (+84)" },
    {
      code: "+62",
      name: "Indonesia",
      flag: "ID",
      displayName: "Indonesia (+62)",
    },
    {
      code: "+852",
      name: "Hong Kong",
      flag: "HK",
      displayName: "Hong Kong (+852)",
    },
    { code: "+886", name: "Taiwan", flag: "TW", displayName: "Taiwan (+886)" },
    {
      code: "+65",
      name: "Singapore",
      flag: "SG",
      displayName: "Singapore (+65)",
    },
    {
      code: "+82",
      name: "South Korea",
      flag: "KR",
      displayName: "South Korea (+82)",
    },
    { code: "+81", name: "Japan", flag: "JP", displayName: "Japan (+81)" },
    { code: "+86", name: "China", flag: "CN", displayName: "China (+86)" },
    { code: "+91", name: "India", flag: "IN", displayName: "India (+91)" },
    { code: "+971", name: "UAE", flag: "AE", displayName: "UAE (+971)" },
    {
      code: "+966",
      name: "Saudi Arabia",
      flag: "SA",
      displayName: "Saudi Arabia (+966)",
    },
  ];

  return (
    <Form.Group className="mb-0">
      {label && (
        <Form.Label style={labelStyle}>
          {label} {required && <span className="text-danger">*</span>}
        </Form.Label>
      )}
      <Row>
        {showCountrySelector && (
          <Col xs={4}>
            <Autocomplete
              options={countries}
              getOptionLabel={(option) => option.displayName}
              value={countries.find((c) => c.code === countryValue) || null}
              onChange={(e, selectedCountry) =>
                onCountryChange!(selectedCountry?.code || "+91")
              }
              onBlur={onCountryBlur}
              disabled={disabled}
              ListboxProps={autocompleteDropdownStyles.ListboxProps}
              renderOption={(props, option) => (
                <Box
                  component="li"
                  sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
                  {...props}
                >
                  <ReactCountryFlag
                    countryCode={option.flag}
                    svg
                    style={{
                      width: "20px",
                      height: "15px",
                      marginRight: "8px",
                    }}
                  />
                  {option.displayName}
                </Box>
              )}
              renderInput={(params) => {
                const selectedCountry = countries.find(
                  (c) => c.code === countryValue,
                );
                return (
                  <BootstrapTextField
                    {...params}
                    id={name}
                    name={name}
                    placeholder="Select Country"
                    size="small"
                    error={countryInvalid}
                    helperText={countryInvalid ? countryErrorMessage : ""}
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: selectedCountry ? (
                        <ReactCountryFlag
                          countryCode={selectedCountry.flag}
                          svg
                          style={{
                            width: "20px",
                            height: "15px",
                            marginRight: "8px",
                          }}
                        />
                      ) : null,
                    }}
                    sx={{
                      background: "#FFF",
                      ...autocompleteDropdownStyles.inputSx,
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: "#ced4da",
                        },
                        "&:hover fieldset": {
                          borderColor: "#ced4da",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#ced4da",
                        },
                      },
                    }}
                  />
                );
              }}
            />
          </Col>
        )}
        <Col xs={showCountrySelector ? 8 : 12}>
          <Form.Control
            type="text"
            id={name}
            name={name}
            value={phoneValue}
            onChange={(e) => onPhoneChange(e.target.value)}
            onBlur={onPhoneBlur}
            placeholder={placeholder}
            isInvalid={phoneInvalid}
            style={inputStyle}
            disabled={disabled}
          />
          {phoneInvalid && phoneErrorMessage && (
            <Form.Control.Feedback type="invalid">
              {phoneErrorMessage}
            </Form.Control.Feedback>
          )}
        </Col>
      </Row>
    </Form.Group>
  );
};

export default PhoneInputComponent;
